import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

type UnsavedChangesContextType = {
    isDirty: boolean;
    setIsDirty: (dirty: boolean) => void;
    isReminderActive: boolean;
    triggerReminder: () => void;
    clearReminder: () => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

export const UnsavedChangesProvider = ({ children }: { children: ReactNode }) => {
    const [isDirty, setIsDirty] = useState(false);
    const [isReminderActive, setIsReminderActive] = useState(false);

    const triggerReminder = useCallback(() => {
        setIsReminderActive(true);
    }, []);

    const clearReminder = useCallback(() => {
        setIsReminderActive(false);
    }, []);

    const handleSetIsDirty = useCallback((dirty: boolean) => {
        setIsDirty(dirty);
        if (!dirty) {
            setIsReminderActive(false);
        }
    }, []);

    return (
        <UnsavedChangesContext.Provider
            value={{
                isDirty,
                setIsDirty: handleSetIsDirty,
                isReminderActive,
                triggerReminder,
                clearReminder,
            }}
        >
            {children}
        </UnsavedChangesContext.Provider>
    );
};

export const useUnsavedChanges = () => {
    const context = useContext(UnsavedChangesContext);
    if (context === undefined) {
        throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
    }
    return context;
};
